import { useEffect, useMemo, useState } from 'react';
import { Line } from 'react-chartjs-2';
import { FaSeedling, FaPlus, FaTimes } from 'react-icons/fa';
import productionRecordsApi from '../../../services/productionRecordsApi';
import monitoringApi from '../../../services/monitoringApi';
import { ChartCard, EmptyState, lineOptions } from '../analytics/analyticsShared';

const EMPTY_FORM = {
  deviceId: '',
  recordDate: new Date().toISOString().slice(0, 10),
  flockAgeDays: '',
  birdCount: '',
  mortalityCount: '0',
  avgWeightGrams: '',
  feedConsumedKg: '',
  notes: '',
};

// Real, manually-logged flock performance data (mortality, weight, feed
// use) per house. This — not the AI report alone — is what actually
// substantiates a productivity claim, since it reflects real outcomes
// rather than model output.
function ProductionRecords() {
  const [devices, setDevices] = useState([]);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [selectedDeviceId, setSelectedDeviceId] = useState('');
  const [syncing, setSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const [devicesRes, recordsRes] = await Promise.all([
        monitoringApi.listDevices(),
        productionRecordsApi.getAll(),
      ]);
      setDevices(devicesRes.devices || []);
      setRecords(recordsRes.records || []);
    } catch (err) {
      /* interceptor toasts */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await productionRecordsApi.create({
        ...form,
        flockAgeDays: Number(form.flockAgeDays),
        birdCount: Number(form.birdCount),
        mortalityCount: Number(form.mortalityCount || 0),
        avgWeightGrams: form.avgWeightGrams ? Number(form.avgWeightGrams) : null,
        feedConsumedKg: form.feedConsumedKg ? Number(form.feedConsumedKg) : null,
      });
      setForm(EMPTY_FORM);
      setShowForm(false);
      load();
    } catch (err) {
      /* interceptor toasts */
    }
  };

  const handleSync = async () => {
    if (!selectedDeviceId) {
      setSyncMessage('Select a device first.');
      return;
    }
    setSyncing(true);
    setSyncMessage('');
    try {
      const res = await productionRecordsApi.syncMortality(selectedDeviceId);
      setSyncMessage(`Synced ${res.synced} day(s) of records from real device data.`);
      load();
    } catch (err) {
      setSyncMessage(typeof err === 'string' ? err : err?.error || 'Sync failed.');
    } finally {
      setSyncing(false);
    }
  };

  const deviceRecords = useMemo(() => {
    if (!selectedDeviceId) return records;
    return records.filter((r) => r.device?._id === selectedDeviceId || r.device?.deviceId === selectedDeviceId);
  }, [records, selectedDeviceId]);

  const trendChart = useMemo(() => {
    const points = [...deviceRecords].reverse();
    if (points.length < 2) return null;
    return {
      labels: points.map((p) => new Date(p.recordDate).toLocaleDateString()),
      datasets: [
        {
          label: 'Mortality rate (%)',
          data: points.map((p) => p.mortalityRatePercent),
          borderColor: '#D85A30',
          backgroundColor: 'rgba(216,90,48,0.1)',
          yAxisID: 'y',
          tension: 0.3,
        },
        {
          label: 'Avg weight (g)',
          data: points.map((p) => p.avgWeightGrams),
          borderColor: '#1e5c33',
          backgroundColor: 'rgba(30,92,51,0.1)',
          yAxisID: 'y1',
          tension: 0.3,
        },
      ],
    };
  }, [deviceRecords]);

  if (loading) {
    return <div className="flex justify-center items-center h-64 text-gray-500">Loading production records…</div>;
  }

  return (
    <div className="p-4 space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-bold text-gray-800 dark:text-white">Production Records</h1>
          <p className="text-xs text-gray-400 mt-1">
            Real, manually-logged flock outcomes — mortality, weight, feed use — per house.
          </p>
        </div>
        <button
          onClick={() => setShowForm((s) => !s)}
          className="flex items-center gap-2 text-sm px-3 py-2 rounded-lg bg-primary text-white hover:opacity-90"
        >
          {showForm ? <FaTimes /> : <FaPlus />} {showForm ? 'Cancel' : 'Log a record'}
        </button>
      </div>

      {showForm && (
        <ChartCard title="Log a production record">
          <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Device / house</label>
              <select
                required
                value={form.deviceId}
                onChange={(e) => setForm({ ...form, deviceId: e.target.value })}
                className="w-full border border-gray-200 rounded-lg px-3 py-2"
              >
                <option value="">Select…</option>
                {devices.map((d) => (
                  <option key={d._id} value={d._id}>
                    {d.houseName || d.name} — {d.farmer?.firstName} {d.farmer?.lastName}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Record date</label>
              <input
                type="date"
                required
                value={form.recordDate}
                onChange={(e) => setForm({ ...form, recordDate: e.target.value })}
                className="w-full border border-gray-200 rounded-lg px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Flock age (days)</label>
              <input
                type="number" required min="0"
                value={form.flockAgeDays}
                onChange={(e) => setForm({ ...form, flockAgeDays: e.target.value })}
                className="w-full border border-gray-200 rounded-lg px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Bird count</label>
              <input
                type="number" required min="0"
                value={form.birdCount}
                onChange={(e) => setForm({ ...form, birdCount: e.target.value })}
                className="w-full border border-gray-200 rounded-lg px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Mortality since last record</label>
              <input
                type="number" min="0"
                value={form.mortalityCount}
                onChange={(e) => setForm({ ...form, mortalityCount: e.target.value })}
                className="w-full border border-gray-200 rounded-lg px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Avg weight (g)</label>
              <input
                type="number" min="0"
                value={form.avgWeightGrams}
                onChange={(e) => setForm({ ...form, avgWeightGrams: e.target.value })}
                className="w-full border border-gray-200 rounded-lg px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Feed consumed (kg)</label>
              <input
                type="number" min="0"
                value={form.feedConsumedKg}
                onChange={(e) => setForm({ ...form, feedConsumedKg: e.target.value })}
                className="w-full border border-gray-200 rounded-lg px-3 py-2"
              />
            </div>
            <div className="md:col-span-3">
              <label className="block text-xs text-gray-500 mb-1">Notes</label>
              <textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                className="w-full border border-gray-200 rounded-lg px-3 py-2"
                rows={2}
              />
            </div>
            <div className="md:col-span-3 flex justify-end">
              <button type="submit" className="text-sm px-4 py-2 rounded-lg bg-primary text-white">Save record</button>
            </div>
          </form>
        </ChartCard>
      )}

      <div className="flex items-center gap-3 flex-wrap">
        <label className="text-xs text-gray-500 mr-2">Filter by device:</label>
        <select
          value={selectedDeviceId}
          onChange={(e) => setSelectedDeviceId(e.target.value)}
          className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm"
        >
          <option value="">All devices</option>
          {devices.map((d) => (
            <option key={d._id} value={d._id}>{d.houseName || d.name}</option>
          ))}
        </select>
        <button
          onClick={handleSync}
          disabled={syncing || !selectedDeviceId}
          className="flex items-center gap-2 text-xs px-3 py-1.5 rounded-lg border border-primary text-primary hover:bg-primary/5 disabled:opacity-50 disabled:cursor-not-allowed"
          title="Roll up real MortalityEvent data written by detection hardware into ProductionRecord entries"
        >
          {syncing ? 'Syncing…' : 'Sync from device'}
        </button>
        {syncMessage && <span className="text-xs text-gray-500">{syncMessage}</span>}
      </div>

      {records.length === 0 ? (
        <EmptyState
          icon={<FaSeedling />}
          title="No production records yet"
          message="Log flock performance data (mortality, weight, feed use) here to build a real, evidence-backed productivity trend over time."
        />
      ) : (
        <>
          {trendChart && (
            <ChartCard title="Trend for selected device">
              <div className="h-64">
                <Line data={trendChart} options={{ ...lineOptions, scales: { y: { type: 'linear', position: 'left' }, y1: { type: 'linear', position: 'right', grid: { drawOnChartArea: false } } } }} />
              </div>
            </ChartCard>
          )}
          <ChartCard title="All records">
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase text-gray-400 border-b border-gray-100">
                    <th className="py-2 pr-4">Date</th>
                    <th className="py-2 pr-4">Device</th>
                    <th className="py-2 pr-4">Farmer</th>
                    <th className="py-2 pr-4">Flock age</th>
                    <th className="py-2 pr-4">Birds</th>
                    <th className="py-2 pr-4">Mortality %</th>
                    <th className="py-2 pr-4">Avg weight (g)</th>
                    <th className="py-2 pr-4">Feed (kg)</th>
                    <th className="py-2 pr-4">Source</th>
                  </tr>
                </thead>
                <tbody>
                  {deviceRecords.map((r) => (
                    <tr key={r._id} className="border-b border-gray-50">
                      <td className="py-2 pr-4 whitespace-nowrap">{new Date(r.recordDate).toLocaleDateString()}</td>
                      <td className="py-2 pr-4">{r.device?.houseName || r.device?.name}</td>
                      <td className="py-2 pr-4">{r.farmer?.firstName} {r.farmer?.lastName}</td>
                      <td className="py-2 pr-4">{r.flockAgeDays}d</td>
                      <td className="py-2 pr-4">{r.birdCount}</td>
                      <td className="py-2 pr-4">{r.mortalityRatePercent ?? '—'}</td>
                      <td className="py-2 pr-4">{r.avgWeightGrams ?? '—'}</td>
                      <td className="py-2 pr-4">{r.feedConsumedKg ?? '—'}</td>
                      <td className="py-2 pr-4">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ring-1 ring-inset ${
                          r.source === 'device_auto' ? 'bg-blue-50 text-blue-700 ring-blue-200' : 'bg-gray-50 text-gray-500 ring-gray-200'
                        }`}>
                          {r.source === 'device_auto' ? 'Device' : 'Manual'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </ChartCard>
        </>
      )}
    </div>
  );
}

export default ProductionRecords;
